import type { FC } from 'react'
import { useState } from 'react'

import { InfoBox } from '@skytech/ui'
import { Table } from '@skytech/ui/Table'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getSortedList } from '@skytech/utils'
import CheckIcon from 'mdi-react/CheckIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { LocalDate } from '@/components/LocalDate'
import RouterLink from '@/components/RouterLink'
import { useIsThreeStepIT } from '@/hooks/access'
import { useSortParams } from '@/hooks/searchParams'
import type { AssetDownPayment, AssetExtended } from '@/modules/admin/resources'

import { ConditionalTrButton } from './ConditionalTrButton'
import { DownPaymentPopup } from './DownPaymentPopup'
import { getOrderIdPath, isDatePassed } from './helpers'

export const SalaryDeductions: FC<{ assetDetails: AssetExtended }> = ({ assetDetails }) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof AssetExtended['downPayments'][number]>()
  const { locale } = useAppContext()

  const salaryDeductions =
    assetDetails?.downPayments
      ?.filter(payment => Date.parse(payment.date))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) || []

  if (salaryDeductions.length === 0) {
    return null
  }
  const isThreeStepIT = useIsThreeStepIT()

  const downPaymentsByDate = salaryDeductions.reduce<Record<string, AssetDownPayment[]>>(
    (acc, downPayment) => {
      const currentDownPaymentList = acc[downPayment.date]
      if (currentDownPaymentList) {
        acc[downPayment.date].push(downPayment)
      } else {
        acc[downPayment.date] = [downPayment]
      }
      return acc
    },
    {}
  )

  const selectedSalaryDeduction = selectedDate ? downPaymentsByDate[selectedDate] : null

  function getIsDeductibleAmountDifferentFromDeductionRowsSum() {
    if (!assetDetails.ecomOrderId || assetDetails.isMigratedIntoEcom) return false

    const nonBuyoutNonOffboardingEcomDownPayments = salaryDeductions.filter(
      payment => !payment.isBuyout && !payment.isOffBoarding && payment.ecomOrderId
    )
    if (nonBuyoutNonOffboardingEcomDownPayments.length === 0) return false

    const downPaymentsSum = Number(
      nonBuyoutNonOffboardingEcomDownPayments
        .reduce((sum, payment) => sum + payment.amount, 0)
        .toFixed(12)
    )
    return downPaymentsSum !== assetDetails.ecomOrderRemainderAmount
  }

  const isSalaryDeductionExplanationVisible = getIsDeductibleAmountDifferentFromDeductionRowsSum()
  const salaryDeductionList = Object.values(downPaymentsByDate).map(downPayments => {
    const amount = downPayments.reduce((sum, downPayment) => {
      if (!downPayment.isActive) return sum

      return sum + Number(downPayment.amount)
    }, 0)
    const isActive = downPayments.some(downPayment => downPayment.isActive)
    const exemplaryDownPayment = downPayments[0]
    return { ...exemplaryDownPayment, amount, isActive }
  })

  const sortedSalaryDeductionList = getSortedList({
    list: salaryDeductionList,
    querySort,
    querySortBy,
    locale,
  })

  return (
    <section className="mx-w-fit m-2 ml-6 w-full">
      <h3>
        <Translation id="Salary Deduction" />
      </h3>

      {isSalaryDeductionExplanationVisible && (
        <InfoBox emphasis="warning" marginBottom={4} marginTop={4}>
          <h5 className="m-0 font-normal">
            <Translation
              id="Salary deduction for this asset does not equal the total deductible amount from the related order. Reasons for this might be some deduction 
being related to other products from the same order, or deduction amounts being changed."
            />
          </h5>
        </InfoBox>
      )}
      <Table>
        <thead>
          <tr>
            <Th id="date" onClick={handleSort}>
              <Translation id="Date" />
            </Th>
            <Th id="amount" onClick={handleSort}>
              <Translation id="Amount" />
            </Th>
            <Th id="isBuyout" className="hidden md:table-cell" onClick={handleSort}>
              <Translation id="Buyout" />
            </Th>
            <Th id="isOffBoarding" className="hidden md:table-cell" onClick={handleSort}>
              <Translation id="Offboarding" />
            </Th>
            {salaryDeductions.some(payment => payment.ecomOrderId) && (
              <Th id="ecomOrderId" className="hidden md:table-cell" onClick={handleSort}>
                <Translation id="Order Id" />
              </Th>
            )}
          </tr>
        </thead>

        <tbody>
          {sortedSalaryDeductionList.map(payment => (
            <ConditionalTrButton
              key={payment.date}
              date={payment.date}
              isEcomDownPayment={Boolean(payment.ecomOrderId)}
              isPaymentActive={payment.isActive}
              onSelectSalaryDeduction={setSelectedDate}
            >
              <Td className={twJoin(!payment.isActive && 'line-through opacity-50')}>
                <LocalDate date={payment.date} />
                {isDatePassed(payment.date) && payment.isActive && (
                  <CheckIcon
                    className={twJoin(
                      'ml-1 h-5 w-5 p-0.5',
                      isThreeStepIT ? 'text-threeStepIT-success' : 'text-green-500'
                    )}
                  />
                )}
              </Td>
              <Td className={twJoin(!payment.isActive && 'line-through opacity-50')}>
                <FormattedNumberWithCurrency amount={payment.amount} currency={payment.currency} />
              </Td>

              <Td className="hidden md:table-cell">
                {payment.isBuyout ? (
                  <CheckIcon
                    className={twJoin(
                      'ml-1 h-5 w-5 p-0.5',
                      isThreeStepIT ? 'text-threeStepIT-success' : 'text-green-500'
                    )}
                  />
                ) : (
                  ''
                )}
              </Td>
              <Td className="hidden md:table-cell">
                {payment.isOffBoarding ? (
                  <CheckIcon
                    className={twJoin(
                      'ml-1 h-5 w-5 p-0.5',
                      isThreeStepIT ? 'text-threeStepIT-success' : 'text-green-500'
                    )}
                  />
                ) : (
                  ''
                )}
              </Td>

              <Td className="hidden md:table-cell">
                {getOrderIdPath(payment) && (
                  <RouterLink to={getOrderIdPath(payment)} hasUnderline={false}>
                    <div className="text-lg/tight">{payment.ecomOrderId} </div>
                  </RouterLink>
                )}
              </Td>
            </ConditionalTrButton>
          ))}
        </tbody>
      </Table>

      {Boolean(selectedDate) && selectedSalaryDeduction && (
        <DownPaymentPopup
          assetId={assetDetails.id}
          downPayments={selectedSalaryDeduction}
          isOpen={true}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </section>
  )
}
